/* CB HIGH VARIANT
 * Custom elements for products with 250+ variants.
 * Uses Shopify's option_values API to defer variant resolution to the server,
 * bypassing the 250-variant cap on product.variants.
 *
 * Extends base classes from variants.js without modifying them.
 * Only loaded on pages using the product.high-variant template.
 */

(function() {
  'use strict';

  /* Wait for variants.js to register the base class before extending it.
   * variants-hv.js appears earlier in the DOM than variants.js, so with defer
   * it executes first. whenDefined() resolves once variants.js runs. */
  customElements.whenDefined('variant-selects').then(function() {

  const BaseSelects = customElements.get('variant-selects');

  /* ── VariantSelectsHV (dropdown picker) ─────────────────────────── */

  class VariantSelectsHV extends BaseSelects {
    constructor() {
      super();
    }

    /**
     * Override: Parse single variant from server-rendered JSON.
     * Skips updateMasterId() and updateVariantStatuses() since the server
     * handles variant resolution and availability.
     */
    onVariantInit() {
      const variantDataEl = this.querySelector('[type="application/json"]');
      this.variantData = variantDataEl ? JSON.parse(variantDataEl.textContent) : [];
      this.currentVariant = this.variantData[0] || null;
      this.updateMedia(1500, 'init');
      if (this.currentVariant) {
        this.renderProductInfo();
        this.updateAttribute(false, !this.currentVariant.available);
      } else {
        this.updateAttribute(true);
      }
    }

    /**
     * Override: Collect data-option-value-id from checked inputs, fetch with
     * ?option_values= param, replace picker + price + property HTML from
     * the server response, then run inherited update methods.
     */
    onVariantChange(event) {
      /* Abort previous fetch if still in flight (handles rapid switching) */
      if (this._fetchController) this._fetchController.abort();
      this._fetchController = new AbortController();

      const changedInputId = event.target.id;
      const optionValueIds = this.getSelectedOptionValueIds();

      /* Bail if the current selection is incomplete. After a re-render that
       * leaves an option group with no checked value (e.g. the newly selected
       * color has no in-stock size), getSelectedOptionValueIds() returns fewer
       * IDs than there are option groups. Firing a partial option_values request
       * resolves to no variant server-side, which would paint the price snippet's
       * fallback over the current (correct) price. Keep the last-known-good state. */
      const optionGroupCount = (this.querySelectorAll('fieldset').length || this.querySelectorAll('select').length);
      if (!optionValueIds.length || optionValueIds.length < optionGroupCount) return;

      const url = `${this.dataset.url}?option_values=${optionValueIds.join(',')}&section_id=${this.dataset.section}`;

      fetch(url, { signal: this._fetchController.signal })
        .then(response => response.text())
        .then(responseText => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');

          const priceId = `product-price-${this.dataset.product}`;

          /* Replace variant picker innerHTML from server response */
          const newPicker = html.querySelector('variant-radios-hv') || html.querySelector('variant-selects-hv');

          /* Resolve the variant from the RESPONSE (not the live DOM) up front,
           * so we can decide whether the render is trustworthy before touching
           * anything. A nil variant means the option_values combination didn't
           * resolve server-side; its price HTML is the snippet's fallback, not a
           * real price, and must not clobber the current price. */
          let resolvedVariant = null;
          const responseDataEl = newPicker ? newPicker.querySelector('[type="application/json"]') : null;
          if (responseDataEl) {
            try {
              resolvedVariant = (JSON.parse(responseDataEl.textContent) || [])[0] || null;
            } catch (e) {
              resolvedVariant = null;
            }
          }

          if (newPicker) {
            this.innerHTML = newPicker.innerHTML;
          }

          /* Replace price HTML only when a real variant resolved. */
          if (resolvedVariant) {
            const newPrice = html.getElementById(priceId);
            const currentPrice = document.getElementById(priceId);
            if (newPrice && currentPrice) {
              currentPrice.innerHTML = newPrice.innerHTML;
            }
          }

          /* Replace property HTML */
          const propertyId = `product-property-${this.dataset.product}`;
          const newProperty = html.getElementById(propertyId);
          const currentProperty = document.getElementById(propertyId);
          if (currentProperty) {
            if (newProperty) {
              currentProperty.innerHTML = newProperty.innerHTML;
              currentProperty.style.display = 'table';
            } else {
              currentProperty.style.display = 'none';
            }
          } else if (newProperty) {
            const insertTarget = document.querySelector('.productView-options');
            if (insertTarget) {
              insertTarget.parentNode.insertBefore(newProperty, insertTarget);
            }
          }

          /* Use the variant already resolved from the response above (the live
           * DOM JSON is identical after the innerHTML swap). */
          this.variantData = resolvedVariant ? [resolvedVariant] : [];
          this.currentVariant = resolvedVariant;

          if (!this.currentVariant) {
            this.updateAttribute(true);
            this.updateStickyAddToCart(true);
          } else {
            this.updateMedia(200, 'change');
            if (!document.querySelector('.featured-product')) {
              this.updateURL();
            }
            this.updateVariantInput();
            this.renderProductInfo();
            this.updateAttribute(false, !this.currentVariant.available);
            this.updateStickyAddToCart(false, !this.currentVariant.available);
            this.updatePickupAvailability();
            this.checkQuantityWhenVariantChange();
          }

          /* Currency conversion */
          if (this.checkNeedToConvertCurrency()) {
            var currencyCode = document.getElementById('currencies')?.querySelector('.active')?.getAttribute('data-currency');
            Currency.convertAll(window.shop_currency, currencyCode, 'span.money', 'money_format');
          }

          /* Replace description from same response (avoids redundant second fetch) */
          var descSelector = '[data-product-description-' + this.dataset.product + ']';
          var newDesc = html.querySelector(descSelector);
          var currentDesc = document.querySelector(descSelector);
          if (newDesc && currentDesc) {
            currentDesc.innerHTML = newDesc.innerHTML;
            if (currentDesc.closest('.toggle-content--height')) {
              currentDesc.style.maxHeight = null;
            }
          }

          /* Update sale badge and remove price visibility-hidden */
          document.getElementById(priceId)?.classList.remove('visibility-hidden');
          this.updateBadgeSale();

          /* Re-focus the changed input by ID */
          if (changedInputId) {
            var refocus = this.querySelector('#' + CSS.escape(changedInputId));
            if (refocus) refocus.focus();
          }
        })
        .catch(function(err) {
          if (err.name === 'AbortError') return;
          console.error('[HV] Variant fetch error:', err);
        });
    }

    /** Read option value IDs from select dropdowns */
    getSelectedOptionValueIds() {
      return Array.from(this.querySelectorAll('select'), function(select) {
        var selected = select.options[select.selectedIndex];
        return selected?.dataset?.optionValueId;
      }).filter(Boolean);
    }

    /** No-op: server handles availability via re-render */
    updateVariantStatuses() {}

    /** No-op: price/property handled in onVariantChange fetch */
    renderProductAjaxInfo() {}

    /** No-op: description handled in onVariantChange fetch */
    updateProductInfo() {}
  }

  customElements.define('variant-selects-hv', VariantSelectsHV);

  /* ── VariantRadiosHV (button/radio picker) ──────────────────────── */

  class VariantRadiosHV extends VariantSelectsHV {
    constructor() {
      super();
    }

    /** Read option value IDs from checked radio inputs */
    getSelectedOptionValueIds() {
      return Array.from(this.querySelectorAll('fieldset'), function(fieldset) {
        var checked = fieldset.querySelector('input[type="radio"]:checked');
        return checked?.dataset?.optionValueId;
      }).filter(Boolean);
    }

    /** Read selected values from radio inputs (inherited methods expect this) */
    updateOptions() {
      var fieldsets = Array.from(this.querySelectorAll('fieldset'));
      this.options = fieldsets.map(function(fieldset) {
        return Array.from(fieldset.querySelectorAll('input')).find(function(radio) { return radio.checked; })?.value;
      });
    }
  }

  customElements.define('variant-radios-hv', VariantRadiosHV);

  }); /* end whenDefined */
})();
/* CB HIGH VARIANT END */
